<script setup lang="ts">
import { RouterView } from 'vue-router';
import { useThemeStore } from '@/stores/theme.store';
import { useAuthStore } from '@/stores/auth.store';
import { onMounted, ref } from 'vue';
import ToastContainer from '@/components/ui/ToastContainer.vue';
import axios from 'axios';

const themeStore = useThemeStore();
const authStore = useAuthStore();
const isReady = ref(false);

// Load branding settings and apply favicon
const loadBranding = async () => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await axios.get(`${apiUrl}/auth/branding-settings`);
    const branding = response.data.data?.branding;

    if (branding?.faviconUrl) {
      // Update or create favicon link
      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'shortcut icon';
        document.head.appendChild(link);
      }
      link.type = 'image/x-icon';
      link.href = branding.faviconUrl;
    }
  } catch (error) {
    // Silently fail - use default favicon
    console.debug('Using default favicon');
  }
};

onMounted(async () => {
  themeStore.initTheme();
  // Load branding in parallel with auth
  await Promise.all([
    authStore.initAuth(),
    loadBranding()
  ]);
  isReady.value = true;
});
</script>

<template>
  <div v-if="!isReady" class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div class="flex flex-col items-center gap-4">
      <div class="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      <span class="text-gray-500 dark:text-gray-400">Loading...</span>
    </div>
  </div>
  <template v-else>
    <RouterView />
    <ToastContainer />
  </template>
</template>
